import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { dashboardAPI } from "../api/dashboard";
import { projectsAPI } from "../api/projects";
import StatCard from "../components/dashboard/StatCard";
import ProjectList from "../components/dashboard/ProjectList";
import RecentActivity from "../components/dashboard/RecentActivity";
import ChartWidget from "../components/dashboard/ChartWidget";
import LoadingSpinner from "../components/common/LoadingSpinner";
import RoleBadge from "../components/common/RoleBadge";
import {
  HomeIcon,
  UsersIcon,
  ClipboardListIcon,
  CubeIcon,
  ClockIcon,
} from "@heroicons/react/outline";

const DashboardPage = () => {
  const { user } = useAuth();
  const [timeframe, setTimeframe] = useState("week");

  // Fetch dashboard data
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", timeframe],
    queryFn: () => dashboardAPI.getOverview(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user's assigned projects (for Sub Manager, Foreman, Worker)
  const { data: userProjectsData } = useQuery({
    queryKey: ["user-projects", user?.id],
    queryFn: () => projectsAPI.getAll({ assigned_to: user?.id }),
    enabled:
      user?.role === "sub_manager" ||
      user?.role === "foreman" ||
      user?.role === "worker",
  });

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="text-6xl mb-4">📊</div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Failed to load dashboard
        </h3>
        <p className="text-slate-600 dark:text-slate-400">
          Please refresh the page or try again later.
        </p>
      </div>
    );
  }

  const stats = data?.data || {};
  const allProjects = stats.projects || {};
  const workers = stats.workers || {};

  // Determine which projects to show based on role
  const getProjectsData = () => {
    const role = user?.role;

    if (role === "super_admin" || role === "project_manager") {
      return allProjects;
    }

    if (role === "sub_manager" || role === "foreman" || role === "worker") {
      const assignedProjects = userProjectsData?.data?.projects || [];
      return {
        total: assignedProjects.length,
        active: assignedProjects.filter((p) => p.status === "active").length,
        planning: assignedProjects.filter((p) => p.status === "planning")
          .length,
        completed: assignedProjects.filter((p) => p.status === "completed")
          .length,
      };
    }

    return allProjects;
  };

  const projects = getProjectsData();

  // Role-based welcome message
  const getWelcomeMessage = () => {
    const role = user?.role;
    const messages = {
      super_admin: "Full system access granted",
      project_manager: "Manage all your projects from here",
      sub_manager: "View and manage your assigned projects",
      foreman: "Oversee site operations and team",
      worker: "View your tasks and track your time",
    };
    return messages[role] || "Welcome to your dashboard";
  };

  // Role-based stats
  const getStatsToShow = () => {
    const role = user?.role;

    if (role === "worker") {
      return [
        {
          title: "My Tasks",
          value: stats.myTasks || 0,
          icon: ClipboardListIcon,
          color: "primary",
          change: 0,
        },
        {
          title: "Hours This Week",
          value: stats.hoursThisWeek || 0,
          icon: ClockIcon,
          color: "success",
          change: 0,
        },
        {
          title: "Active Projects",
          value: projects.active || 0,
          icon: HomeIcon,
          color: "warning",
          change: 0,
        },
      ];
    }

    if (role === "foreman") {
      return [
        {
          title: "Team Members",
          value: stats.teamSize || 0,
          icon: UsersIcon,
          color: "primary",
          change: 0,
        },
        {
          title: "Active Tasks",
          value: stats.activeTasks || 0,
          icon: ClipboardListIcon,
          color: "warning",
          change: 0,
        },
        {
          title: "Hours Today",
          value: stats.hoursToday || 0,
          icon: ClockIcon,
          color: "success",
          change: 0,
        },
        {
          title: "My Projects",
          value: projects.active || 0,
          icon: HomeIcon,
          color: "info",
          change: 0,
        },
      ];
    }

    // Super Admin, Project Manager, Sub Manager
    return [
      {
        title: "Total Projects",
        value: projects.total || 0,
        icon: HomeIcon,
        color: "primary",
        change: 12,
      },
      {
        title: "Active Projects",
        value: projects.active || 0,
        icon: ClipboardListIcon,
        color: "success",
        change: 8,
      },
      {
        title: "Active Workers",
        value: workers.active || 0,
        icon: UsersIcon,
        color: "warning",
        change: 5,
      },
      {
        title: "Low Stock Items",
        value: stats.stockAlerts || 0,
        icon: CubeIcon,
        color: "danger",
        change: -2,
      },
    ];
  };

  const statsToShow = getStatsToShow();

  // Chart data - only for Admin/PM
  const projectProgressData = stats.projectProgress || [
    { name: "Active", value: projects.active || 0 },
    { name: "Planning", value: projects.planning || 0 },
    { name: "On Hold", value: projects.on_hold || 0 },
    { name: "Completed", value: projects.completed || 0 },
  ];

  const laborDistributionData = stats.laborDistribution || [
    { name: "Active Workers", value: workers.active || 0 },
    {
      name: "Inactive Workers",
      value: (workers.total || 0) - (workers.active || 0),
    },
  ];

  // Get recent projects based on role
  const getRecentProjects = () => {
    if (
      user?.role === "sub_manager" ||
      user?.role === "foreman" ||
      user?.role === "worker"
    ) {
      return userProjectsData?.data?.projects?.slice(0, 5) || [];
    }
    return stats.recentProjects || [];
  };

  const recentProjects = getRecentProjects();

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
              Welcome back, {user?.full_name?.split(" ")[0] || "User"}! 👋
            </h1>
            <RoleBadge role={user?.role} className="text-sm px-3 py-1" />
          </div>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {getWelcomeMessage()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsToShow.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            change={stat.change || 0}
          />
        ))}
      </div>

      {/* Charts Section - Only for Admin/PM */}
      {(user?.role === "super_admin" || user?.role === "project_manager") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartWidget
            title="Project Progress"
            type="bar"
            data={projectProgressData}
            height={300}
          />
          <ChartWidget
            title="Labor Distribution"
            type="pie"
            data={laborDistributionData}
            height={300}
          />
        </div>
      )}

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProjectList projects={recentProjects} />
        <RecentActivity activities={stats.recentActivity || []} />
      </div>
    </div>
  );
};

export default DashboardPage;
